import React, { useEffect, useRef } from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Alert,
  Box,
  Chip,
  MenuItem,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Controller, useWatch } from 'react-hook-form';
import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import type { InvoiceContractorSectionProps } from './InvoiceContractorSection.types';
import { isEuVatNumber, getCountryFromVatNumber } from '../../utils/crossBorderVatHelper';
import { invoiceLogger } from '../../utils/invoiceLogger';
import { COUNTRY_OPTIONS } from '../../../../Utils/countries/countries';

const InvoiceContractorSection: React.FC<InvoiceContractorSectionProps> = ({
  control,
  errors,
  onBuyerNipChange,
}) => {

  // Watch useDifferentRecipient dla warunkowego renderowania
  const useDifferentRecipient = useWatch({
    control,
    name: 'useDifferentRecipient',
  });

  // Watch buyerNip for EU VAT detection
  const buyerNip = useWatch({
    control,
    name: 'buyerNip',
  });

  const buyerCountryIso = useWatch({
    control,
    name: 'buyerCountryIso',
  });

  // Detect EU VAT number
  const isEuVat = isEuVatNumber(buyerNip);
  const countryCode = getCountryFromVatNumber(buyerNip);

  // Track last processed NIP to prevent infinite loops
  const lastProcessedNipRef = useRef<string | null | undefined>(null);
  // Debounce timer to prevent re-renders during typing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Notify parent component when buyerNip changes (for VAT auto-suggestion)
  // DEBOUNCED to prevent focus loss during typing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce: wait 500ms after user stops typing
    debounceTimerRef.current = setTimeout(() => {
      if (buyerNip !== lastProcessedNipRef.current && onBuyerNipChange) {
        invoiceLogger.debug('[InvoiceContractorSection] buyerNip changed (debounced):', {
          from: lastProcessedNipRef.current,
          to: buyerNip,
        });
        lastProcessedNipRef.current = buyerNip;
        onBuyerNipChange(buyerNip);
      }
    }, 500); // 500ms debounce delay

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [buyerNip, onBuyerNipChange]);

  return (
    <AccordionCard title="Kontrahent" icon={<InfoIcon color="primary" />}>
      <Grid container spacing={2}>
        {/* EU VAT Detection Alert */}
        {isEuVat && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <strong>Wykryto kontrahenta UE:</strong>
                <Chip
                  label={`${countryCode} - Odwrotne obciążenie VAT`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Box mt={0.5} fontSize="0.875rem">
                Pozycje faktury zostaną automatycznie ustawione na stawkę{' '}
                <strong>VatEU (0% WDT)</strong>. Kontrahent rozliczy VAT we własnym kraju (reverse
                charge - Art. 42 ustawy o VAT).
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Nabywca */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="buyerName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nabywca – nazwa / imię i nazwisko"
                fullWidth
                error={!!errors.buyerName}
                helperText={errors.buyerName?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="buyerStreetNo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nabywca – ulica i numer"
                placeholder="np. Jaśminowa 33 m. 4"
                fullWidth
                error={!!errors.buyerStreetNo}
                helperText={errors.buyerStreetNo?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Controller
            name="buyerNip"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="NIP (opcjonalnie)"
                placeholder="np. DE123456789"
                fullWidth
                error={!!errors.buyerNip}
                helperText={
                  errors.buyerNip?.message ||
                  (isEuVat
                    ? `Numer VAT-UE (${countryCode})`
                    : buyerCountryIso?.toUpperCase() === 'PL'
                      ? 'Dla kraju PL: wpisz 10 cyfr bez prefiksu PL (np. 1234567890)'
                      : 'Format: 10 cyfr (NIP) lub VAT-UE (np. DE123456789)')
                }
                InputProps={{
                  endAdornment: isEuVat ? (
                    <Chip label={countryCode} size="small" color="primary" sx={{ height: 24 }} />
                  ) : null,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="buyerCountryIso"
            control={control}
            rules={{ deps: 'buyerNip' }}
            render={({ field }) => (
              <Autocomplete
                options={COUNTRY_OPTIONS}
                getOptionLabel={(o) => `${o.code} — ${o.label}`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                value={COUNTRY_OPTIONS.find((o) => o.code === (field.value || 'PL').toUpperCase()) ?? null}
                onChange={(_, selected) => field.onChange(selected?.code ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kraj nabywcy (ISO)"
                    error={!!errors.buyerCountryIso}
                    helperText={
                      errors.buyerCountryIso?.message || 'Wartość używana w eligibility Mode C'
                    }
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="isBuyerVatEu"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Status VAT-UE nabywcy"
                fullWidth
                value={field.value ? 'true' : 'false'}
                onChange={(event) => field.onChange(event.target.value === 'true')}
                error={!!errors.isBuyerVatEu}
                helperText={
                  errors.isBuyerVatEu?.message ||
                  'Dla kraju PL backend i tak normalizuje wartość do false'
                }
              >
                <MenuItem value="false">false</MenuItem>
                <MenuItem value="true">true</MenuItem>
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="buyerIsJst"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Nabywca jest JST"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="buyerAddress"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nabywca – pozostały adres (kod, miasto)"
                placeholder="np. 30-000 Kraków"
                fullWidth
                error={!!errors.buyerAddress}
                helperText={errors.buyerAddress?.message}
              />
            )}
          />
        </Grid>

        {/* Przełącznik odbiorcy */}
        <Grid size={{ xs: 12 }}>
          <Controller
            name="useDifferentRecipient"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Odbiorca inny niż nabywca"
              />
            )}
          />
        </Grid>

        {/* Odbiorca (opcjonalnie) */}
        {useDifferentRecipient && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="recipientName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Odbiorca – nazwa / imię i nazwisko"
                    fullWidth
                    error={!!errors.recipientName}
                    helperText={errors.recipientName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="recipientStreetNo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Odbiorca – ulica i numer"
                    placeholder="np. Jaśminowa 33 m. 4"
                    fullWidth
                    error={!!errors.recipientStreetNo}
                    helperText={errors.recipientStreetNo?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Controller
                name="recipientNip"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="NIP odbiorcy (opcjonalnie)"
                    fullWidth
                    error={!!errors.recipientNip}
                    helperText={errors.recipientNip?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="recipientAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Odbiorca – pozostały adres (kod, miasto)"
                    placeholder="np. 30-000 Kraków"
                    fullWidth
                    error={!!errors.recipientAddress}
                    helperText={errors.recipientAddress?.message}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </AccordionCard>
  );
};

export default InvoiceContractorSection;
